import { Button, Card, Checkbox, Col, Divider, Flex, Form, FormProps, Row } from 'antd';
import FormElement from 'components/formElement/formElement';
import FormPassword from 'components/formPassword/formPassword';
import PhoneInputComponent, { PhoneInputReturn } from 'components/phoneInput/phoneInput';
import NotificationService from 'core/services/notification';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { checkPhoneValidation } from 'utils/validators/phone';
import { ContactPersonRequestPayload } from '../models/contactProfile';
import { setOtpTTL, submitContactPersonalInfo } from '../services/contactProfileService';
import authService from 'core/services/auth';
import { useDispatch } from 'react-redux';
import { setAdmin } from 'stores/useAdminStore';
import Recaptcha from 'components/recaptcha/recaptcha';
import { checkArabicValidation } from 'utils/validators/arabic';
import { checkEnglishValidation } from 'utils/validators/english';
import ReCAPTCHA from 'react-google-recaptcha';

export default function ContactRegisterForm() {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const recaptchaRef = useRef<ReCAPTCHA>(null);

	const [registerForm] = Form.useForm();

	const [haveEmployee, setHaveEmployee] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	type FieldType = ContactPersonRequestPayload;

	const validatePassword = (_: any, value: string) => {
		if (!value || registerForm.getFieldValue('password') === value) {
			return Promise.resolve();
		}
		return Promise.reject(new Error(t('COMMON.PASSWORDS_NOT_MATCH')));
	};

	const onChangePhone = ({ number, dialCode }: PhoneInputReturn) => {
		registerForm.setFieldsValue({
			phone_number: number,
			phone_country_code: dialCode,
		});
	};

	const onChangeHefthEmployee = (event: any) => {
		setHaveEmployee(event.target.checked);
		registerForm.setFieldsValue({
			employee_code_entered_by_company: event.target.checked ? 1 : 0,
		});
	};

	const validatePhone = (_: any, value: any) => {
		if (checkPhoneValidation(registerForm.getFieldValue('phone_country_code'), value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.PHONE_INVALID')));
		else return Promise.reject();
	};

	const validateArabic = (_: any, value: string) => {
		if (checkArabicValidation(_, value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.ARABIC_ONLY')));
		else return Promise.reject();
	};

	const validateEnglish = (_: any, value: string) => {
		if (checkEnglishValidation(_, value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.ENGLISH_ONLY')));
		else return Promise.reject();
	};

	const onFinish: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsLoading(true);

			const response = await submitContactPersonalInfo(values);

			authService.setToken(response?.data?.token, true);
			authService.setData(true, [], 'contact_person');
			dispatch(setAdmin(response?.data?.company_user));
			recaptchaRef.current?.reset();

			setOtpTTL(response?.data?.otp_ttl);

			window.scrollTo({ top: 0 });

			setTimeout(() => {
				navigate('otp');
			}, 300);
		} catch (error: any) {
			setIsLoading(false);

			const errorMessage = error?.response?.data?.message || t('COMMON.SUBMIT_ERROR');
			NotificationService.error(t('COMMON.ERROR'), errorMessage);

			recaptchaRef.current?.reset();
			registerForm.setFieldValue('recaptcha_token', null);

			if (error?.status === 422)
				Object.entries(error?.response?.data?.data?.errors).forEach(([key, value]) => {
					registerForm.setFields([
						{
							name: key,
							errors: value as string[],
						},
					]);
				});
		}
	};

	return (
		<>
			<motion.h2
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{
					duration: 0.5,
					delay: 0.3,
				}}
				key="title">
				{t('REGISTER.CONTACT_PERSON')}
			</motion.h2>

			<motion.p
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{
					duration: 0.5,
					delay: 0.5,
				}}
				key="subTitle"
				className="text-light">
				{t('REGISTER.SUBTITLE')}
			</motion.p>

			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{
					duration: 0.5,
					delay: 0.8,
				}}
				key="form"
				style={{ textAlign: 'start' }}
				className="container">
				<Card>
					<Form
						name="registerForm"
						onFinish={onFinish}
						form={registerForm}
						layout="vertical"
						autoComplete="off"
						scrollToFirstError
						initialValues={{
							employee_code_entered_by_company: 0,
							employee_code: null,
						}}>
						<Row>
							<Col span={24} className="p-2">
								<h4>{t('REGISTER.PERSON_INF')}</h4>
							</Col>
							<Col span={24} md={12} className="p-2">
								<FormElement
									noAutoComplete
									name="name"
									type="text"
									label={t('REGISTER.REPRESENTATIVE_NAME_AR')}
									rules={[
										{ required: true, message: t('COMMON.FIELD_REQUIRED') },
										{
											pattern: /^[^\s\d!@#$%^&*()_+={}\[\]:;"'<>?,.\\/|`~]+(?:\s+[^\s\d!@#$%^&*()_+={}\[\]:;"'<>?,.\\/|`~]+){2,}\s*$/,
											message: t('REGISTER.THREE_WORDS_VALIDATION'),
										},
										{ validator: validateArabic },
									]}
								/>
							</Col>
							<Col span={24} md={12} className="p-2">
								<FormElement
									noAutoComplete
									name="name_en"
									type="text"
									label={t('REGISTER.REPRESENTATIVE_NAME_EN')}
									rules={[
										{ required: true, message: t('COMMON.FIELD_REQUIRED') },
										{
											pattern: /^[^\s\d!@#$%^&*()_+={}\[\]:;"'<>?,.\\/|`~]+(?:\s+[^\s\d!@#$%^&*()_+={}\[\]:;"'<>?,.\\/|`~]+){2,}\s*$/,
											message: t('REGISTER.THREE_WORDS_VALIDATION'),
										},
										{ validator: validateEnglish },
									]}
								/>
							</Col>
							<Col span={24} md={12} className="p-2">
								<FormElement
									noAutoComplete
									name="email"
									label={t('REGISTER.REPRESENTATIVE_EMAIL')}
									type="email"
									rules={[
										{ required: true, message: t('COMMON.FIELD_REQUIRED') },
										{ type: 'email', message: t('COMMON.EMAIL_INVALID') },
									]}
								/>
							</Col>
							<Col span={24} md={12} className="p-2">
								<FormElement
									noAutoComplete
									name="national_id"
									type="number"
									label={t('REGISTER.REPRESENTATIVE_NATIONAL_ID')}
									rules={[
										{ required: true, message: t('COMMON.FIELD_REQUIRED') },
										{ len: 10, message: t('COMMON.NATIONAL_ID_LENGTH') },
									]}
								/>
							</Col>
							<Col span={24} md={12} className="p-2">
								<FormPassword
									noAutoComplete
									name="password"
									label={t('COMMON.PASSWORD')}
									rules={[
										{ required: true, message: t('COMMON.PASSWORD_REQUIRED') },
										{ min: 8, message: t('COMMON.PASSWORD_MIN_8') },
									]}
									style={{ maxWidth: 'unset' }}
								/>
							</Col>
							<Col span={24} md={12} className="p-2">
								<FormPassword
									noAutoComplete
									name="password_confirmation"
									label={t('COMMON.PASSWORD_CONFIRMATION')}
									rules={[{ required: true, message: t('COMMON.PASSWORD_CONFIRMATION_REQUIRED') }, { validator: validatePassword }]}
									style={{ maxWidth: 'unset' }}
								/>
							</Col>
							<Col span={24} md={12} className="p-2">
								<Form.Item name="phone_number" rules={[{ required: true, message: t('COMMON.PHONE_REQUIRED') }, { validator: validatePhone }]}>
									<PhoneInputComponent noAutoComplete onChange={onChangePhone} label={t('REGISTER.REPRESENTATIVE_PHONE_NUMBER')} />
								</Form.Item>
								<Form.Item name="phone_country_code" hidden noStyle />
							</Col>
						</Row>

						<Row>
							<Col span={24} className="p-2">
								<Form.Item name="employee_code_entered_by_company" valuePropName="checked" noStyle>
									<Checkbox onChange={onChangeHefthEmployee}>{t('REGISTER.DO_YOU_HAVE_EMPLOYEE')}</Checkbox>
								</Form.Item>
							</Col>

							<Col span={24} className="px-2">
								{haveEmployee && (
									<FormElement
										noAutoComplete
										name="employee_code"
										type="text"
										label={t('REGISTER.HEFTH_EMPLOYEE_CODE')}
										rules={[{ required: haveEmployee, message: 'Required' }]}
									/>
								)}
							</Col>
						</Row>

						<Divider />

						<Row>
							<Col span={24}>
								<h4>{t('REGISTER.COMPANY_MANAGER')}</h4>
							</Col>

							<Col span={24} md={12} className="p-2">
								<FormElement
									noAutoComplete
									name="company_manager_name"
									type="text"
									label={t('COMMON.NAME_AR')}
									rules={[
										{ required: true, message: t('COMMON.FIELD_REQUIRED') },
										{
											pattern: /^[^\s\d!@#$%^&*()_+={}\[\]:;"'<>?,.\\/|`~]+(?:\s+[^\s\d!@#$%^&*()_+={}\[\]:;"'<>?,.\\/|`~]+){2,}\s*$/,
											message: t('REGISTER.THREE_WORDS_VALIDATION'),
										},
										{ validator: validateArabic },
									]}
								/>
							</Col>
							<Col span={24} md={12} className="p-2">
								<FormElement
									noAutoComplete
									name="company_manager_name_en"
									type="text"
									label={t('COMMON.NAME_EN')}
									rules={[
										{ required: true, message: t('COMMON.FIELD_REQUIRED') },
										{
											pattern: /^[^\s\d!@#$%^&*()_+={}\[\]:;"'<>?,.\\/|`~]+(?:\s+[^\s\d!@#$%^&*()_+={}\[\]:;"'<>?,.\\/|`~]+){2,}\s*$/,
											message: t('REGISTER.THREE_WORDS_VALIDATION'),
										},
										{ validator: validateEnglish },
									]}
								/>
							</Col>
							<Col span={24} md={12} className="p-2">
								<FormElement
									noAutoComplete
									name="company_manager_email"
									type="text"
									label={t('COMMON.EMAIL')}
									rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
								/>
							</Col>
						</Row>

						<Divider />

						<Row>
							<Col span={24}>
								<h4>{t('REGISTER.AUTHORIZED_SIGNATORY_INF')}</h4>
							</Col>

							<Col span={24} md={12} className="p-2">
								<FormElement
									noAutoComplete
									name="authorized_signatory_name"
									type="text"
									label={t('COMMON.NAME_AR')}
									rules={[
										{ required: true, message: t('COMMON.FIELD_REQUIRED') },
										{
											pattern: /^[^\s\d!@#$%^&*()_+={}\[\]:;"'<>?,.\\/|`~]+(?:\s+[^\s\d!@#$%^&*()_+={}\[\]:;"'<>?,.\\/|`~]+){2,}\s*$/,
											message: t('REGISTER.THREE_WORDS_VALIDATION'),
										},
										{ validator: validateArabic },
									]}
								/>
							</Col>
						</Row>

						<Divider />

						<Flex justify="flex-end">
							<Form.Item name="recaptcha_token" rules={[{ required: true, message: t('COMMON.RECAPTCHA_REQUIRED') }]}>
								<Recaptcha ref={recaptchaRef} onSubmit={(token) => registerForm.setFieldValue('recaptcha_token', token)} />
							</Form.Item>
						</Flex>

						<Row justify="end">
							<Col className="py-4 px-2">
								<Button type="primary" ghost htmlType="submit" loading={isLoading}>
									{t('COMMON.NEXT')}
								</Button>
							</Col>
						</Row>
					</Form>
				</Card>
			</motion.div>
		</>
	);
}
